import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {CustomEmailValidator} from '../../validators/custom-email.validator';
import {AuthState} from '../../states/auth/auth.state';
import {Observable} from 'rxjs/internal/Observable';
import {CheckUser} from '../../states/auth/auth.actions';
import {take, withLatestFrom} from 'rxjs/operators';
import {ResetProfileSuccess, UpdateAccountPIN} from '../../states/profile/profile.actions';
import {ProfileState} from '../../states/profile/profile.state';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ResetPinStepsEnum} from '../../interfaces/ResetPinStepsEnum';

@UntilDestroy()
@Component({
  selector: 'app-reset-pin-dialog',
  templateUrl: './reset-pin-dialog.component.html',
  styleUrls: ['./reset-pin-dialog.component.scss'],
})
export class ResetPinDialogComponent implements OnInit {
  @Input() show: boolean;

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() afterUpdate: EventEmitter<boolean> = new EventEmitter();

  currentStep: ResetPinStepsEnum = ResetPinStepsEnum.Auth;
  ResetPinStepsEnum = ResetPinStepsEnum;

  displayToggleInner: boolean;
  hidePIN = true;
  inputsType = 'password';

  checkUserForm: UntypedFormGroup;

  @ViewChild('passwordInput') passwordInput;
  @ViewChild('setPinButton') setPinButton;
  @ViewChild('pinInput') pinInput;

  pinValue: string;
  pinErrorMessage: string;
  checkErrorMessage: string;

  @Select(AuthState.loading) loadingAuth$: Observable<boolean | undefined>;
  @Select(ProfileState.loading) loadingProfile$: Observable<boolean | undefined>;
  @Select(AuthState.activeUserId) userId$: Observable<string>;
  @Select(ProfileState.success) profileSuccess$: Observable<boolean>;
  @Select(AuthState.success) authSuccess$: Observable<boolean>;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef
  ) {
    this.checkUserForm = this.fb.group({
      email: ['', [Validators.required, CustomEmailValidator]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.checkUserForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.checkErrorMessage) {
          this.checkErrorMessage = null;
        }
      });
  }

  checkUser(): void {
    if (this.checkUserForm.valid) {
      this.checkErrorMessage = null;
      this.store.dispatch(new CheckUser(this.checkUserForm.value))
        .pipe(withLatestFrom(this.authSuccess$))
        .subscribe(([_, success]) => {
          if (success) {
            this.currentStep = ResetPinStepsEnum.PIN;
            this.cd.detectChanges();
          } else {
            this.checkErrorMessage = 'Invalid credentials or access to pharmacy not granted';
          }
        });
    }
  }

  pinEntered(pin: string): void {
    this.pinValue = pin;
    this.pinErrorMessage = '';
    setTimeout(() => this.setPinButton.focus());
  }

  setPIN(): void {
    if (this.pinValue) {
      this.store.dispatch(new UpdateAccountPIN(this.pinValue))
        .pipe(take(1), withLatestFrom(this.profileSuccess$))
        .subscribe(([_, success]) => {
          if (success) {
            this.currentStep = ResetPinStepsEnum.Done;
            setTimeout(() => {
              this.afterUpdate.emit(success);
              this.resetDialog();
            }, 2000);
          } else {
            this.pinErrorMessage = 'You must choose a different PIN.';
            setTimeout(() => this.pinInput.clearInputs());
          }
        });
    }
  }

  resetDialog(): void {
    this.checkUserForm.reset();
    this.store.dispatch(new ResetProfileSuccess());
    this.currentStep = ResetPinStepsEnum.Auth;
    this.pinValue = '';
    this.pinErrorMessage = '';
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  togglePIN(): void {
    this.hidePIN = !this.hidePIN;
    this.inputsType = this.inputsType === 'password' ? 'text' : 'password';
  }
}
